import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { isNil, isUndefined, maxBy, uniqBy, keys, max, round, groupBy } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getLabel } from '../../utils/StoreUtils'
import ProgressBar from '../../components/progress/ProgressBar'
import {
    getLocalizationPicto,
    calculateAverage,
    searchMaxAnalysis,
    filterValid,
    filterResult,
    filterQuantification,
    searchMinAnalysis,
    searchP90Analysis,
    searchP90Value,
    searchMinValue,
    searchMaxValue,
    ResultWithStatus,
    REMARK_CODES,
} from '../../utils/AnalyseUtils'
import { getDate } from '../../utils/DateUtil'
import { nbPerPageLabel } from '../../referencial/constants/ReferencialConstants'
import SampleModal from '../../quality/components/qualityResearch/SampleModal'
import { exportFile } from '../../utils/ExportDataUtil'
import Card from '../card/Card'
import { getI18nTitleDataLength } from '../../utils/StringUtil'
import { Accordion, Grid, IconButton, Stack, Tooltip } from '@mui/material'
import useListIndexed from 'utils/customHook/useListIndexed'
import useBoolean from 'utils/customHook/useBoolean'
import PaginatedList from 'components/list/PaginatedList'
import { ParameterGraphModal } from 'quality/components/qualityComponents/ParameterGraph'
import { AccordionTitle } from 'components/styled/Accordions'
import { NewTable, WrapperAccordionDetails } from 'components/datatable/NewTable'
import { calculateThresholdsResult, getMarkerColor } from 'utils/QualityThresholdUtils'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import { FileDownload, Info, InsertChart } from '@mui/icons-material'
import { blackColor, whiteColor } from 'utils/constants/ColorTheme'
import { QualityThresholdType } from 'quality/shapes/QualityThresholdShapes'
import { NITRATES, NITRATES_PESTICIDES, NO_CONTAMINATION, PESTICIDES } from 'catchment/constants/CatchmentConstant'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'

const EXPORT_ULTRA_LIGHT = ['code', 'catchment', 'date', 'parameterCode', 'parameter', 'unitCode', 'unit', 'result']
const SORTING_TYPE = {
    MAX: '1',
    MIN: '2',
    AVERAGE: '3',
    P90: '4',
    FIX: '5',
}
const NO_ANALYSIS_VALUE = '-1'
const CONTAMINATION_VALUE = {
    MISSING_DATA: -1,
    NO_CONTAMINATION: 0,
    NITRATES_OR_PESTICIDES: 1,
    NITRATES_AND_PESTICIDES: 2,
}

const orderContaminationLevel = (analyse) => {
    const contamination = analyse.contamination
    if (contamination === NO_CONTAMINATION) return CONTAMINATION_VALUE.NO_CONTAMINATION
    if (contamination === NITRATES || contamination === PESTICIDES) return CONTAMINATION_VALUE.NITRATES_OR_PESTICIDES
    if (contamination === NITRATES_PESTICIDES) return CONTAMINATION_VALUE.NITRATES_AND_PESTICIDES
    return CONTAMINATION_VALUE.MISSING_DATA
}

const CollapsibleTable = ({
    catchment = {},
    displayAnalysis = {},
    analysis = [],
    isOnlyOneParameter = false,
    thresholds = [],
    openSampleModal = () => {},
}) => {
    const dispatch = useDispatch()

    const {
        parameters,
        units,
        settings,
        contributors,
        qualifications,
        status,
        laboratories,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        settings: store.AdministrationReducer.settings,
        contributors: store.ContributorReducer.contributors,
        qualifications: store.QualityReducer.qualifications,
        status: store.QualityReducer.status,
        laboratories: store.ContributorReducer.laboratories,
    }), shallowEqual)

    const parametersIndexed = useListIndexed(parameters, 'code')
    const unitsIndexed = useListIndexed(units, 'code')
    const contributorsIndexed = useListIndexed(contributors, 'id')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const ultraLightMode = useMemo(() => max(analysis.map(a => keys(a).length)) < 20, [analysis])

    const {
        value: isOpen,
        setFalse: close,
        setTrue: open,
    } = useBoolean(false)

    const formattedAnalysis = useMemo(() => {
        return analysis.map(a => {
            return {
                labo: { value: contributorsIndexed[a.labo]?.labelDisplay ?? '' },
                date: { value: getDate(a.analysisDate) },
                statusAndResult: {
                    value: ultraLightMode ? (
                        <Grid container spacing={0.5} alignItems='center' sx={{ width: '100%' }}>
                            <Grid size='grow' sx={{ backgroundColor: a.color }}>
                                { a.value }
                            </Grid>
                        </Grid>
                    ) : (
                        <ResultWithStatus
                            qualification={a.qualification}
                            status={a.status}
                            result={a.value}
                            color={a.color}
                        />
                    ),
                },
                unit: { value: unitsIndexed[a.unit]?.symbol },
                nullValue: { value: getLocalizationPicto(a.localization) },
                id: a.id,
            }
        })
    }, [analysis, contributorsIndexed, unitsIndexed, ultraLightMode])

    const nbParameter = useMemo(() => uniqBy(analysis, 'parameter').length, [analysis])
    const parameter = useMemo(() => isOnlyOneParameter ? parametersIndexed[displayAnalysis.parameter] : {}, [isOnlyOneParameter, parametersIndexed, displayAnalysis])

    const validAnalysis = useMemo(() => filterValid(filterResult(analysis)), [analysis])
    const maxAnalysis = useMemo(() => searchMaxAnalysis(analysis), [analysis])
    const averageValue = useMemo(() => calculateAverage(analysis, settings), [analysis, settings])

    const nbAnalysis = useMemo(() => analysis.length, [analysis])
    const nbQuantification = useMemo(() => filterQuantification(validAnalysis).length, [validAnalysis])

    const exportAnalysis = useCallback(() => {
        const data = analysis.map(a => ({
            code: catchment.code,
            catchment: catchment.name,
            date: getDate(a.analysisDate),
            parameterCode: { value: a.parameter, cellType: 'right' },
            parameter: getLabel(parameters, a.parameter),
            unitCode: { value: a.unit, cellType: 'right' },
            unit: getLabel(units, a.unit, 'symbol'),
            result: { value: a.value, color: getMarkerColor(a.color), cellType: 'right' },
            status: getLabel(status, a.status),
            qualification: getLabel(qualifications, a.qualification),
            labo: getLabel(laboratories, a.labo),
        }))
        const dataWithHeaders = data.length ? [
            { ...data[0], headers: ultraLightMode ? EXPORT_ULTRA_LIGHT : keys(data[0]) },
            ...data.slice(1),
        ] : []
        exportFile({
            data: dataWithHeaders,
            exportType: 'xlsx',
            titleFile: `${catchment.code}_${catchment.name}`,
        })
    }, [qualifications, analysis, units, status, ultraLightMode, laboratories, parameters, catchment])

    const headers = useMemo(() => {
        const statusAndResult = { key: 'statusAndResult', value: `${formattedAnalysis.length} ${getI18nTitleDataLength(i18n.result, i18n.results, formattedAnalysis.length)}` }
        return ultraLightMode ? ['date', statusAndResult, 'unit', 'nullValue'] : ['labo', 'date', statusAndResult, 'unit', 'nullValue']
    }, [formattedAnalysis.length, ultraLightMode])

    return (
        <>
            <Accordion>
                <AccordionTitle
                    fontColor={blackColor}
                    color={whiteColor}
                    title={(
                        <Grid container columnSpacing={1} rowSpacing={0.5} alignItems='center' sx={{ fontSize: '13px' }}>
                            <Grid size={3}>
                                <Stack>
                                    <span>{catchment?.code ? `[${catchment?.code}]` : ''}</span>
                                    <span>{catchment?.name}</span>
                                    <span>{`${i18n.sandreCode} : ${catchment.sandreCode || '/'}`}</span>
                                </Stack>
                            </Grid>
                            <Grid size={2} sx={{ backgroundColor: displayAnalysis.color }}>
                                <Stack>
                                    <span>
                                        {getDate(displayAnalysis.analysisDate)}
                                    </span>
                                    <span>
                                        {displayAnalysis.remark === REMARK_CODES.TRACES && i18n.trace || !isUndefined(displayAnalysis.value) && `${displayAnalysis.value} ${unitsIndexed[displayAnalysis.unit]?.symbol ?? ''}` || ''}
                                    </span>
                                </Stack>
                            </Grid>
                            <Grid size={4}>
                                {isOnlyOneParameter && (
                                    <Stack>
                                        <span>
                                            {(showSandreCode ? parameter?.labelWithCode : parameter?.name) ?? ''}
                                        </span>
                                        <span>
                                            {i18n.unit}: {`${unitsIndexed[displayAnalysis.unit]?.symbol ?? '/'}`}
                                        </span>
                                        <span>
                                            {i18n.max}: {`${maxAnalysis?.value ? round(maxAnalysis.value, 4) : ''}`}
                                        </span>
                                        <span>
                                            {i18n.average}: {!isNil(averageValue) && !isNaN(averageValue) ? averageValue : ''}
                                        </span>
                                    </Stack>
                                )}
                                {!isOnlyOneParameter && (
                                    <Grid size={12}>
                                        <span>{`${nbParameter} ${nbParameter < 2 ? i18n.parameter : i18n.parameters}`}</span>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid size={2}>
                                <ProgressBar
                                    progress={nbAnalysis !== 0 ? nbQuantification * 100 / nbAnalysis : 0}
                                    tooltip={ultraLightMode ? i18n.quantificationsNotCalculated : `${nbQuantification} ${i18n.quantificationsRatio} ${nbAnalysis} ${i18n.results}`}
                                />
                            </Grid>
                            <Grid size={1}>
                                <Tooltip title={i18n.accessToTheFile}>
                                    <IconButton>
                                        <Info
                                            sx={{ color: blackColor }}
                                            onClick={e => {
                                                e.stopPropagation()
                                                dispatch(push(`/station/catchment/${catchment?.id}`))
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={i18n.export}>
                                    <IconButton>
                                        <FileDownload
                                            sx={{ color: blackColor }}
                                            onClick={e => {
                                                e.stopPropagation()
                                                exportAnalysis()
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={i18n.graph}>
                                    <IconButton>
                                        <InsertChart
                                            sx={{ color: blackColor }}
                                            onClick={e => {
                                                e.stopPropagation()
                                                open()
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    )}
                />
                <NewTable
                    rows={formattedAnalysis}
                    headers={headers}
                    rowsPerPageOptions={nbPerPageLabel}
                    onClickRow={({ id }) => openSampleModal(id)}
                    WrapperComponent={WrapperAccordionDetails}
                />
            </Accordion>
            <ParameterGraphModal
                isOpen={isOpen}
                closeGraph={close}
                analysis={analysis}
                thresholds={thresholds}
                hideActions={!isOnlyOneParameter}
            />
        </>
    )
}

CollapsibleTable.propTypes = {
    catchment: PropTypes.instanceOf(CatchmentDto),
    displayAnalysis: PropTypes.shape({}),
    thresholds: PropTypes.arrayOf(PropTypes.number),
    analysis: PropTypes.arrayOf(PropTypes.shape({})),
    isOnlyOneParameter: PropTypes.bool,
    openSampleModal: PropTypes.func,
}

const CartographyCatchmentPanel = ({
    catchments = [],
    files = [],
    stationsPanelTitle = '',
    thresholds = [],
    analysis = [],
    sortingType,
    fixedAnalysis = [],
}) => {
    const {
        settings,
        catchmentPoints,
        qualitometers,
        units,
        qualifications,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.settings,
        catchmentPoints: store.CatchmentReducer.catchmentPoints,
        qualitometers: store.QualityReducer.qualitometersLight,
        units: store.UnitReducer.units,
        qualifications: store.QualityReducer.qualifications,
    }), shallowEqual)

    const isOnlyOneParameter = useMemo(() => uniqBy(analysis, 'parameter')?.length === 1, [analysis])
    const ultraLightMode = useMemo(() => max(analysis.map(a => keys(a).length)) < 20, [analysis])

    const {
        value: isOpen,
        setFalse: close,
        setTrue: open,
    } = useBoolean(false)
    const [selectedAnalysis, setSelectedAnalysis] = useState()

    const sandreCatchments = useMemo(() => catchments.filter(c => files.some(f => f.shortName?.split('-')?.[1] === c.sandreCode?.toString())), [catchments, files])
    const otherCatchments = useMemo(() => catchments.filter(c => !sandreCatchments.map(s => s.id).includes(c.id)), [catchments, sandreCatchments])
    const catchmentsIds = useMemo(() => [...sandreCatchments, ...otherCatchments].map(c => c.id), [sandreCatchments, otherCatchments])

    const groupByCatchment = useMemo(() => catchments.reduce((acc, c) => {
        const points = catchmentPoints.filter(catchment => catchment.id === c.id)
        const qualito = qualitometers.filter(q => points.map(p => p.codeWithoutDesignation).includes(q.code))
        acc[c.id] = analysis.filter(a => qualito.map(q => q.id).includes(a.qualitometer) && a.value && a.value !== NO_ANALYSIS_VALUE)
        return acc
    }, {}), [catchments, catchmentPoints, qualitometers, analysis])

    const getDisplayedAnalysis = useCallback(listAnalysis => {
        const validAnalysis = ultraLightMode ? listAnalysis : filterValid(filterResult(listAnalysis))
        if (sortingType === SORTING_TYPE.MAX) {
            return searchMaxAnalysis(validAnalysis)
        } else if (sortingType === SORTING_TYPE.MIN) {
            return searchMinAnalysis(validAnalysis)
        } else if (sortingType === SORTING_TYPE.AVERAGE) {
            const average = calculateAverage(validAnalysis, settings)
            const analyse = validAnalysis[0]
            const averageAnalyse = {
                result: average,
                parameter: analyse?.parameter,
                unit: analyse?.unit,
                fraction: analyse?.fraction,
                support: analyse?.support,
                qualitometer: analyse?.qualitometer,
            }
            return {
                ...averageAnalyse,
                ...calculateThresholdsResult(averageAnalyse, thresholds),
                color: analyse?.color ?? calculateThresholdsResult(averageAnalyse, thresholds).color,
            }
        } else if (sortingType === SORTING_TYPE.P90) {
            return searchP90Analysis(validAnalysis)
        } else if (sortingType === SORTING_TYPE.FIX) {
            const analysisQualitometers = groupBy(validAnalysis, STATION_TYPE_NAME.qualitometer)
            const qualitosFixedAnalysis = keys(analysisQualitometers).map(key => fixedAnalysis.find(a => a?.qualitometer?.toString() === key))
            return maxBy(qualitosFixedAnalysis, orderContaminationLevel) ?? maxBy(qualitosFixedAnalysis, 'result')
        }
        return maxBy(listAnalysis, 'analysisDate')
    }, [settings, sortingType, ultraLightMode, thresholds, fixedAnalysis])

    const analyse = useMemo(() => {
        return analysis.find(a => a.id === selectedAnalysis)
    }, [analysis, selectedAnalysis])

    const exportStations = useCallback(() => {
        const data = catchmentsIds.map(id => {
            const catchment = catchments.find(c => c.id === id)
            const catchmentAnalysis = groupByCatchment[id]
            const displayAnalysis = getDisplayedAnalysis(catchmentAnalysis)

            const validAnalysis = ultraLightMode ? catchmentAnalysis : filterValid(filterResult(catchmentAnalysis))
            const p90 = searchP90Value(validAnalysis)
            const min = searchMinValue(validAnalysis)
            const maxValue = searchMaxValue(validAnalysis)
            const average = calculateAverage(validAnalysis, settings)

            return {
                code: catchment.code,
                catchment: catchment.name,
                lastAnalysis: { value: getDate(displayAnalysis?.analysisDate), format: 'dd/MM/yyy', cellType: 'date' },
                result: { value: displayAnalysis?.value, color: displayAnalysis?.color, cellType: 'right' },
                min: { value: min, cellType: 'right' },
                max: { value: maxValue, cellType: 'right' },
                average: { value: average, cellType: 'right' },
                percentile90: { value: p90, cellType: 'right' },
                codeUnit: { value: displayAnalysis?.unit, cellType: 'right' },
                unit: getLabel(units, displayAnalysis?.unit, 'symbol'),
                qualification: getLabel(qualifications, displayAnalysis?.qualification),
            }
        })
        const dataWithHeaders = data.length ? [
            { ...data[0], headers: keys(data[0]) },
            ...data.slice(1),
        ] : []
        exportFile({
            data: dataWithHeaders,
            exportType: 'xlsx',
            titleFile: i18n.research,
        })
    }, [units, getDisplayedAnalysis, ultraLightMode, qualifications, settings, catchmentsIds, groupByCatchment, catchments])

    const actions = useMemo(() => [{
        iconName: 'file_download',
        tooltip: i18n.export,
        onClick: exportStations,
    }], [exportStations])

    return analysis.length === 0 ? (
        <h5 className='center-align'>{i18n.noAssociatedStations}</h5>
    ) : (
        <div>
            <Card title={`${stationsPanelTitle} (${catchmentsIds.length}) - ${sandreCatchments.length} ${i18n.validatedSandreCode}`} actions={actions}>
                <Grid container spacing={1} padding={1}>
                    <PaginatedList
                        nbElements={catchmentsIds.length}
                        nbElementsByPage={7}
                    >
                        {
                            (startId, endId) => {
                                const subList = catchmentsIds.slice(startId, endId)
                                return subList.map(id => {
                                    const catchmentAnalysis = groupByCatchment[id]
                                    const displayAnalysis = getDisplayedAnalysis(catchmentAnalysis)
                                    return (
                                        <Grid size={12} key={id}>
                                            <CollapsibleTable
                                                catchment={catchments.find(c => c.id === id)}
                                                displayAnalysis={displayAnalysis}
                                                analysis={catchmentAnalysis}
                                                isOnlyOneParameter={isOnlyOneParameter}
                                                thresholds={thresholds}
                                                openSampleModal={analyseId => {
                                                    setSelectedAnalysis(analyseId)
                                                    open()
                                                }}
                                            />
                                        </Grid>
                                    )
                                })
                            }
                        }
                    </PaginatedList>
                </Grid>
            </Card>
            <SampleModal
                isOpen={isOpen}
                close={close}
                analyse={analyse}
            />
        </div>
    )
}

CartographyCatchmentPanel.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    files: PropTypes.arrayOf(PropTypes.shape({})),
    stationsPanelTitle: PropTypes.string,
    thresholds: PropTypes.arrayOf(PropTypes.oneOfType(PropTypes.number, QualityThresholdType)),
    analysis: PropTypes.arrayOf(PropTypes.shape({})),
    sortingType: PropTypes.string,
    fixedAnalysis: PropTypes.arrayOf(PropTypes.shape({})),
}

export default CartographyCatchmentPanel